/**
 * Voluntary leave / un-RSVP: stamps left_at on the active group_members row.
 * "Active" = latest row per (group_id, user_id) by joined_at (then id) has left_at null.
 * Archiving and creator handoff are handled by a database trigger — see the note
 * at the bottom of this file.
 */

export async function getActiveMembershipRow(supabase, groupId, userId) {
  const { data: rows, error } = await supabase
    .from('group_members')
    .select('*')
    .eq('group_id', groupId)
    .eq('user_id', userId);

  if (error) return { row: null, error };
  if (!rows?.length) return { row: null, error: null };

  const sorted = [...rows].sort((a, b) => {
    const ta = new Date(a.joined_at || 0).getTime();
    const tb = new Date(b.joined_at || 0).getTime();
    if (tb !== ta) return tb - ta;
    return String(b.id || '').localeCompare(String(a.id || ''));
  });
  const latest = sorted[0];
  if (latest.left_at != null) return { row: null, error: null };
  return { row: latest, error: null };
}

/**
 * Sets left_at on the active membership row (voluntary leave).
 * @returns {{ leavingRole: string, error: Error|null }}
 */
export async function insertVoluntaryLeaveRow(supabase, groupId, userId) {
  const { row: active, error: selErr } = await getActiveMembershipRow(supabase, groupId, userId);
  if (selErr) return { leavingRole: null, error: selErr };
  if (!active) return { leavingRole: null, error: new Error('No active membership') };

  const ts = new Date().toISOString();
  const { error: updateErr } = await supabase
    .from('group_members')
    .update({ left_at: ts })
    .eq('id', active.id);

  if (updateErr) return { leavingRole: active.role, error: updateErr };
  return { leavingRole: active.role, error: null };
}

// Creator handoff and group archiving live in the
// trg_cleanup_group_when_last_member_leaves trigger, which fires on the left_at
// update. Client-side copies of that logic used to run here as well and
// produced duplicate "you're now the manager" notifications.
